import React, { useState, useMemo, useCallback } from "react";
import {
  Search,
  RefreshCw,
  Download,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Package,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  HelpCircle,
  Plus,
  Check,
} from "lucide-react";
import NdStar from "../common/NdStar";
import {
  useManufacturingStockAnalysisQuery,
  GetManufacturingStockAnalysisRequest,
  TimePeriodFilter,
  ManufacturingStockSortBy,
  ManufacturingStockSeverity,
  formatNumber,
  formatPercentage,
  formatWarehouseStock,
  calculateTimePeriodRange,
  getTimePeriodDisplayText,
  toGeneratedTimePeriod,
} from "../../api/hooks/useManufacturingStockAnalysis";
import { getAuthenticatedApiClient } from "../../api/client";
import { exportToXlsx } from "../../utils/exportToXlsx";
import { useToast } from "../../contexts/ToastContext";
import CatalogDetail from "./CatalogDetail";
import { PAGE_CONTAINER_HEIGHT } from "../../constants/layout";
import { usePlanningList } from "../../contexts/PlanningListContext";
import PlanningListPanel from "../common/PlanningListPanel";
import { GridColumn, GridHeader, ColumnChooser, useGridLayout } from '../../features/grid-layout';
import { ManufacturingStockItemDto } from '../../api/hooks/useManufacturingStockAnalysis';
import { useScreenView } from '../../telemetry/useScreenView';

const SALES_MULTIPLIER_COOKIE = "manufacturing-stock-sales-multiplier";

const getCookie = (name: string): string | null => {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? match.split("=")[1] : null;
};

const setCookie = (name: string, value: string): void => {
  document.cookie = `${name}=${value}; path=/; max-age=31536000`;
};

const readSalesMultiplierCookie = (): number => {
  const raw = getCookie(SALES_MULTIPLIER_COOKIE);
  if (raw === null) return 1.0;
  const parsed = parseFloat(raw);
  if (isNaN(parsed) || parsed < 0.1 || parsed > 3.0) return 1.0;
  return parsed;
};

const ManufacturingStockAnalysis: React.FC = () => {
  useScreenView('Manufacturing', 'ManufacturingStockAnalysis');
  // State for filters
  const [filters, setFilters] = useState<GetManufacturingStockAnalysisRequest>({
    timePeriod: TimePeriodFilter.Q9M,
    productFamily: undefined,
    criticalItemsOnly: true,
    majorItemsOnly: true,
    adequateItemsOnly: false,
    unconfiguredOnly: false,
    searchTerm: "",
    pageNumber: 1,
    pageSize: 20,
    sortBy: ManufacturingStockSortBy.OverstockPercentage,
    sortDescending: false,
    salesMultiplier: readSalesMultiplierCookie(),
  });

  // State for product detail modal
  const [selectedProductCode, setSelectedProductCode] = useState<string | null>(
    null,
  );
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // State for collapsible sections
  const [isControlsCollapsed, setIsControlsCollapsed] = useState(false);

  // State for expandable rows (ProductFamily subgrids)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // State for loaded ProductFamily data
  const [subgridData, setSubgridData] = useState<Record<string, any[]>>({});
  const [loadingSubgrids, setLoadingSubgrids] = useState<Set<string>>(
    new Set(),
  );
  const [isExporting, setIsExporting] = useState(false);

  const { showError } = useToast();

  // Query for stock analysis data
  const { data, isLoading, error, isRefetching, refetch } =
    useManufacturingStockAnalysisQuery(filters);

  // Planning list functionality
  const { addItem: addToPlanningList, removeItem: removeFromPlanningList, items: planningListItems } = usePlanningList();

  // Memoized data for performance
  const tableData = useMemo(() => data?.items || [], [data?.items]);
  const summary = useMemo(() => data?.summary, [data?.summary]);

  // Pagination calculations
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / filters.pageSize!);

  // Handler for filter changes
  const handleFilterChange = (
    newFilters: Partial<GetManufacturingStockAnalysisRequest>,
  ) => {
    setFilters((prev) => ({ ...prev, ...newFilters, pageNumber: 1 }));
  };

  // Handler for sales multiplier change
  const handleSalesMultiplierChange = (delta: number) => {
    setFilters((prev) => {
      const current = prev.salesMultiplier ?? 1.0;
      const next = Math.round((current + delta) * 10) / 10;
      const clamped = Math.min(3.0, Math.max(0.1, next));
      setCookie(SALES_MULTIPLIER_COOKIE, clamped.toString());
      return { ...prev, salesMultiplier: clamped, pageNumber: 1 };
    });
  };

  // Handler for pagination
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setFilters((prev) => ({ ...prev, pageNumber: newPage }));
    }
  };

  // Handler for page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setFilters((prev) => ({ ...prev, pageSize: newPageSize, pageNumber: 1 }));
  };

  // Handler for sorting
  const handleSort = (column: ManufacturingStockSortBy) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortDescending: prev.sortBy === column ? !prev.sortDescending : true,
      pageNumber: 1,
    }));
  };

  // Handler for time period change
  const handleTimePeriodChange = (timePeriod: TimePeriodFilter) => {
    if (timePeriod === TimePeriodFilter.CustomPeriod) {
      const range = calculateTimePeriodRange(TimePeriodFilter.Q9M);
      handleFilterChange({
        timePeriod,
        customFromDate: range?.fromDate || undefined,
        customToDate: range?.toDate || undefined,
      });
    } else {
      handleFilterChange({
        timePeriod,
        customFromDate: undefined,
        customToDate: undefined,
      });
    }
  };

  // Export functionality
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const apiClient = await getAuthenticatedApiClient();

      const result = await apiClient.manufacturingStockAnalysis_GetStockAnalysis(
        toGeneratedTimePeriod(filters.timePeriod),
        filters.customFromDate,
        filters.customToDate,
        filters.productFamily,
        filters.criticalItemsOnly,
        filters.majorItemsOnly,
        filters.adequateItemsOnly,
        filters.unconfiguredOnly,
        filters.searchTerm,
        undefined, // pageNumber — export returns all matching rows; the pre-refactor
        undefined, // pageSize  — query string builder never sent pageNumber/pageSize either
        filters.sortBy,
        filters.sortDescending,
        filters.salesMultiplier,
        true, // isExport
      );

      const today = new Date().toISOString().split("T")[0];
      await exportToXlsx(
        result.items ?? [],
        [
          { header: "Kód", value: (row: ManufacturingStockItemDto) => row.code },
          { header: "Název", value: (row: ManufacturingStockItemDto) => row.name },
          { header: "Sklad aktuální", value: (row: ManufacturingStockItemDto) => row.currentStock },
          { header: "Sklad ERP", value: (row: ManufacturingStockItemDto) => row.erpStock },
          { header: "Sklad E-shop", value: (row: ManufacturingStockItemDto) => row.eshopStock },
          { header: "Sklad transport", value: (row: ManufacturingStockItemDto) => row.transportStock },
          { header: "Primární zdroj skladu", value: (row: ManufacturingStockItemDto) => row.primaryStockSource },
          { header: "Rezervace", value: (row: ManufacturingStockItemDto) => row.reserve },
          { header: "Plánováno", value: (row: ManufacturingStockItemDto) => row.planned },
          { header: "Prodeje v období", value: (row: ManufacturingStockItemDto) => row.salesInPeriod },
          { header: "Denní prodeje", value: (row: ManufacturingStockItemDto) => row.dailySalesRate },
          { header: "Optimální dny (nastavení)", value: (row: ManufacturingStockItemDto) => row.optimalDaysSetup },
          { header: "Dní skladu", value: (row: ManufacturingStockItemDto) => row.stockDaysAvailable },
          { header: "Minimální sklad", value: (row: ManufacturingStockItemDto) => row.minimumStock },
          { header: "Přebytečné (%)", value: (row: ManufacturingStockItemDto) => row.overstockPercentage },
          { header: "Velikost dávky", value: (row: ManufacturingStockItemDto) => row.batchSize },
          { header: "Produktová rodina", value: (row: ManufacturingStockItemDto) => row.productFamily },
          { header: "Závažnost", value: (row: ManufacturingStockItemDto) => row.severity },
          { header: "Nakonfigurováno", value: (row: ManufacturingStockItemDto) => row.isConfigured },
        ],
        `manufacturing-stock-analysis-${today}.xlsx`,
      );
    } catch {
      showError("Export selhal", "Nepodařilo se stáhnout data pro export.");
    } finally {
      setIsExporting(false);
    }
  }, [filters, showError]);

  // Planning list functionality
  const isInPlanningList = (productCode: string | undefined) => {
    return planningListItems.some(item => item.productCode === productCode);
  };

  const handleTogglePlanningList = (item: any, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation(); // Prevent row click
    }

    if (isInPlanningList(item.code)) {
      removeFromPlanningList(item.code);
    } else {
      addToPlanningList({ code: item.code, name: item.name });
    }
  };

  // Column definitions for grid layout
  const columns = useMemo((): GridColumn<ManufacturingStockItemDto>[] => [
    {
      id: 'product',
      header: 'Produkt',
      canHide: false,
      canReorder: false,
      align: 'left',
      minWidth: 200,
      defaultWidth: 276,
      renderCell: (item) => {
        const hasSubItems = shouldShowExpandButton(item);
        const isExpanded = !!(item.productFamily && expandedRows.has(item.productFamily));
        return (
          <div className="flex items-center">
            {hasSubItems ? (
              <button
                className="expand-button flex-shrink-0 p-1 mr-2 text-gray-400 hover:text-gray-600 dark:text-graphite-faint dark:hover:text-graphite-muted focus:outline-none"
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.productFamily && item.code) {
                    handleRowExpand(item.productFamily, item.code);
                  }
                }}
                title={isExpanded ? 'Skrýt ostatní produkty řady' : 'Zobrazit ostatní produkty řady'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : (
              <div className="w-6 mr-2"></div>
            )}
            <div className={`w-1 h-8 mr-3 rounded-sm ${getSeverityStripColor(item.severity)}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 text-xs text-gray-900 dark:text-graphite-text font-medium">
                <span className="truncate">{item.name}</span>
                <NdStar isNd={item.isNd} />
              </div>
              <div className="text-xs text-gray-500 dark:text-graphite-muted">{item.code}</div>
              {item.productFamily && <div className="text-xs text-gray-400 dark:text-graphite-faint">{item.productFamily}</div>}
            </div>
          </div>
        );
      },
    },
    {
      id: 'indicator',
      header: '',
      canHide: false,
      canReorder: false,
      canResize: false,
      align: 'center',
      minWidth: 60,
      defaultWidth: 60,
      renderCell: (item) => (
        <button
          onClick={(e) => handleTogglePlanningList(item, e)}
          className={`p-1 rounded transition-colors ${
            isInPlanningList(item.code)
              ? 'text-green-600 dark:text-emerald-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
              : 'text-gray-400 dark:text-graphite-faint hover:text-indigo-600 dark:hover:text-graphite-accent hover:bg-indigo-50 dark:hover:bg-graphite-accent/10'
          }`}
          title={isInPlanningList(item.code) ? 'Odebrat ze seznamu k plánování' : 'Přidat do seznamu k plánování'}
        >
          {isInPlanningList(item.code) ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </button>
      ),
    },
    {
      id: 'currentStock',
      header: 'Sklad',
      sortBy: ManufacturingStockSortBy.CurrentStock,
      align: 'right',
      minWidth: 60,
      defaultWidth: 120,
      cellClassName: 'text-xs',
      renderCell: (item) => (
        <div className={`font-bold ${getStockValueColorClass(item.severity)}`}>
          {formatWarehouseStock(item)}
        </div>
      ),
    },
    {
      id: 'vyrobeno',
      header: 'Vyrobeno',
      align: 'right',
      minWidth: 60,
      defaultWidth: 120,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) =>
        (item.manufacturedStock || 0) > 0 ? (
          <div className="font-bold">{formatNumber(item.manufacturedStock, 0)}</div>
        ) : (
          <span className="text-gray-400 dark:text-graphite-faint">—</span>
        ),
    },
    {
      id: 'reserve',
      header: 'Rezerv',
      sortBy: ManufacturingStockSortBy.Reserve,
      align: 'right',
      minWidth: 60,
      defaultWidth: 120,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) =>
        (item.reserve || 0) > 0 ? (
          <div className="font-bold">{formatNumber(item.reserve, 0)}</div>
        ) : (
          <span className="text-gray-400 dark:text-graphite-faint">—</span>
        ),
    },
    {
      id: 'quarantine',
      header: 'Karant.',
      sortBy: ManufacturingStockSortBy.Quarantine,
      align: 'right',
      minWidth: 60,
      defaultWidth: 120,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) =>
        (item.quarantine || 0) > 0 ? (
          <div className="font-bold text-orange-700 dark:text-orange-300">{formatNumber(item.quarantine, 0)}</div>
        ) : (
          <span className="text-gray-400 dark:text-graphite-faint">—</span>
        ),
    },
    {
      id: 'planned',
      header: 'Plán',
      sortBy: ManufacturingStockSortBy.Planned,
      align: 'right',
      minWidth: 60,
      defaultWidth: 120,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) =>
        (item.planned || 0) > 0 ? (
          <div className="font-bold">{formatNumber(item.planned, 0)}</div>
        ) : (
          <span className="text-gray-400 dark:text-graphite-faint">—</span>
        ),
    },
    {
      id: 'salesInPeriod',
      header: 'Prodeje',
      sortBy: ManufacturingStockSortBy.SalesInPeriod,
      align: 'right',
      minWidth: 60,
      defaultWidth: 144,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) => <>{formatNumber(item.salesInPeriod, 0)}</>,
    },
    {
      id: 'dailySales',
      header: 'Prod/den',
      sortBy: ManufacturingStockSortBy.DailySales,
      align: 'right',
      minWidth: 100,
      defaultWidth: 144,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) => <div>{formatNumber(item.dailySalesRate, 2)}</div>,
    },
    {
      id: 'stockDaysAvailable',
      header: 'NS',
      sortBy: ManufacturingStockSortBy.StockDaysAvailable,
      align: 'right',
      minWidth: 90,
      defaultWidth: 120,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) => (
        <div className="font-bold">
          {(item.stockDaysAvailable ?? 0) > 999 ? '∞' : formatNumber(item.stockDaysAvailable, 0)}
        </div>
      ),
    },
    {
      id: 'overstockPercentage',
      header: 'NS %',
      sortBy: ManufacturingStockSortBy.OverstockPercentage,
      align: 'right',
      minWidth: 90,
      defaultWidth: 120,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) => <>{formatPercentage(item.overstockPercentage)}</>,
    },
    {
      id: 'minimumStock',
      header: 'Min',
      sortBy: ManufacturingStockSortBy.MinimumStock,
      align: 'right',
      minWidth: 90,
      defaultWidth: 120,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) => <>{formatNumber(item.minimumStock, 0)}</>,
    },
    {
      id: 'optimalDaysSetup',
      header: 'Nastavení',
      sortBy: ManufacturingStockSortBy.OptimalDaysSetup,
      align: 'right',
      minWidth: 90,
      defaultWidth: 120,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) => <>{(item.optimalDaysSetup ?? 0) > 0 ? `${item.optimalDaysSetup} dní` : '—'}</>,
    },
    {
      id: 'batchSize',
      header: 'ks/šarže',
      sortBy: ManufacturingStockSortBy.BatchSize,
      align: 'right',
      minWidth: 80,
      defaultWidth: 96,
      cellClassName: 'text-xs text-gray-900 dark:text-graphite-text',
      renderCell: (item) => <>{item.batchSize || '—'}</>,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [expandedRows, loadingSubgrids]);

  const { orderedColumns, columnState, setColumnOrder, setColumnWidthLive, commitColumnWidth, toggleColumnVisibility, resetLayout } =
    useGridLayout('manufacturing-stock-analysis', columns);

  // Get row background color based on severity (like in PurchaseStockAnalysis)
  const getRowColorClass = (
    severity: ManufacturingStockSeverity | undefined,
    isSubgridRow: boolean = false,
  ) => {
    // Handle both string and numeric enum values
    const severityStr = String(severity);
    
    // Subgrid rows have slightly darker background for better visual separation
    if (isSubgridRow) {
      switch (severityStr) {
        case "Critical":
        case "0":
          return "bg-red-100/50 hover:bg-red-100/70 dark:bg-red-900/30 dark:hover:bg-red-900/40";
        case "Major":
        case "1":
          return "bg-orange-100/50 hover:bg-orange-100/70 dark:bg-orange-900/30 dark:hover:bg-orange-900/40";
        case "Minor":
        case "2":
          return "bg-yellow-100/50 hover:bg-yellow-100/70 dark:bg-amber-900/30 dark:hover:bg-amber-900/40";
        case "Adequate":
        case "3":
          return "bg-green-100/50 hover:bg-green-100/70 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/40";
        case "Unconfigured":
        case "4":
          return "bg-gray-100/50 hover:bg-gray-100/70 dark:bg-graphite-surface-2 dark:hover:bg-white/5";
        default:
          return "bg-gray-100/50 hover:bg-gray-100/70 dark:bg-graphite-surface-2 dark:hover:bg-white/5";
      }
    } else {
      switch (severityStr) {
        case "Critical":
        case "0":
          return "bg-red-50/30 hover:bg-red-50/50 dark:bg-red-900/20 dark:hover:bg-red-900/30";
        case "Major":
        case "1":
          return "bg-orange-50/30 hover:bg-orange-50/50 dark:bg-orange-900/20 dark:hover:bg-orange-900/30";
        case "Minor":
        case "2":
          return "bg-yellow-50/30 hover:bg-yellow-50/50 dark:bg-amber-900/20 dark:hover:bg-amber-900/30";
        case "Adequate":
        case "3":
          return "bg-green-50/30 hover:bg-green-50/50 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/30";
        case "Unconfigured":
        case "4":
          return "bg-gray-50/30 hover:bg-gray-50/50 dark:bg-graphite-surface-2 dark:hover:bg-white/5";
        default:
          return "hover:bg-gray-50 dark:hover:bg-white/5";
      }
    }
  };

  // Handle severity filter click from summary cards - checkbox behavior
  const handleSeverityFilterClick = (severity: ManufacturingStockSeverity) => {
    switch (severity) {
      case ManufacturingStockSeverity.Critical:
        handleFilterChange({ criticalItemsOnly: !filters.criticalItemsOnly });
        break;
      case ManufacturingStockSeverity.Major:
        handleFilterChange({ majorItemsOnly: !filters.majorItemsOnly });
        break;
      case ManufacturingStockSeverity.Adequate:
        handleFilterChange({ adequateItemsOnly: !filters.adequateItemsOnly });
        break;
      case ManufacturingStockSeverity.Unconfigured:
        handleFilterChange({ unconfiguredOnly: !filters.unconfiguredOnly });
        break;
    }
  };

  // Modal handlers for product detail
  const handleRowClick = (item: any, event?: React.MouseEvent) => {
    // Don't open detail if clicking expand button
    if (
      event?.target &&
      (event.target as HTMLElement).closest(".expand-button")
    ) {
      return;
    }
    setSelectedProductCode(item.code);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetail = () => {
    setIsDetailModalOpen(false);
    setSelectedProductCode(null);
  };

  // Handler for expanding/collapsing rows
  const handleRowExpand = async (
    productFamily: string,
    currentProductCode: string,
  ) => {
    const newExpandedRows = new Set(expandedRows);

    if (expandedRows.has(productFamily)) {
      // Collapse row
      newExpandedRows.delete(productFamily);
      setExpandedRows(newExpandedRows);
    } else {
      // Expand row and load data if not already loaded
      newExpandedRows.add(productFamily);
      setExpandedRows(newExpandedRows);

      // Check if data is already loaded
      if (!subgridData[productFamily] && !loadingSubgrids.has(productFamily)) {
        // Start loading
        const newLoadingSubgrids = new Set(loadingSubgrids);
        newLoadingSubgrids.add(productFamily);
        setLoadingSubgrids(newLoadingSubgrids);

        try {
          // Fetch products for this ProductFamily
          const apiClient = await getAuthenticatedApiClient();
          const data = await apiClient.manufacturingStockAnalysis_GetStockAnalysis(
            undefined, // timePeriod
            undefined, // customFromDate
            undefined, // customToDate
            productFamily,
            undefined, // criticalItemsOnly
            undefined, // majorItemsOnly
            undefined, // adequateItemsOnly
            undefined, // unconfiguredOnly
            undefined, // searchTerm
            undefined, // pageNumber
            100, // pageSize — get all products in family
            undefined, // sortBy
            undefined, // sortDescending
            undefined, // salesMultiplier
            false, // isExport
          );

          // Filter out the current product from subgrid
          const filteredItems = (data.items ?? []).filter(
            (item) => item.code !== currentProductCode,
          );

          setSubgridData((prev) => ({
            ...prev,
            [productFamily]: filteredItems,
          }));
        } catch (error) {
          console.error("Error loading product family data:", error);
        } finally {
          // Remove from loading set
          const newLoadingSubgrids = new Set(loadingSubgrids);
          newLoadingSubgrids.delete(productFamily);
          setLoadingSubgrids(newLoadingSubgrids);
        }
      }
    }
  };

  // Check if row should show expand button (all products with ProductFamily)
  const shouldShowExpandButton = (item: any) => {
    return !!item.productFamily;
  };

  // Subgrid component for ProductFamily products
  const ProductFamilySubgrid: React.FC<{
    productFamily: string;
    isLoading: boolean;
  }> = ({ productFamily, isLoading }) => {
    const items = subgridData[productFamily] || [];

    if (isLoading) {
      return (
        <tr className="bg-gray-50 dark:bg-graphite-surface-2">
          <td colSpan={12} className="px-4 py-4">
            <div className="flex items-center justify-center">
              <RefreshCw className="h-4 w-4 animate-spin text-gray-400 dark:text-graphite-faint mr-2" />
              <span className="text-sm text-gray-600 dark:text-graphite-muted">
                Načítání produktů stejné řady...
              </span>
            </div>
          </td>
        </tr>
      );
    }

    if (items.length === 0) {
      return (
        <tr className="bg-gray-50 dark:bg-graphite-surface-2">
          <td colSpan={12} className="px-4 py-3">
            <div className="text-sm text-gray-500 dark:text-graphite-muted text-center">
              Žádné další produkty v této řadě
            </div>
          </td>
        </tr>
      );
    }

    return (
      <>
        {items.map((subItem) => (
          <tr
            key={`sub-${subItem.code}`}
            className={`${getRowColorClass(subItem.severity)} cursor-pointer transition-colors duration-150 border-l-8 border-indigo-300 dark:border-graphite-accent`}
            onClick={(e) => handleRowClick(subItem, e)}
            title="Klikněte pro zobrazení detailu produktu"
          >
            {/* Product Info - matching main table column width */}
            <td
              className="px-4 py-3 whitespace-nowrap"
              style={{ minWidth: "200px", width: "23%" }}
            >
              <div className="flex items-center">
                <div className="w-10 mr-2"></div>
                {/* Color strip based on severity */}
                <div
                  className={`w-1 h-6 mr-3 rounded-sm ${getSeverityStripColor(subItem.severity)}`}
                ></div>
                <div className="flex-1 min-w-0 pl-6">
                  <div className="flex items-center gap-1 text-xs text-gray-700 dark:text-graphite-muted font-medium">
                    <span className="truncate">{subItem.name}</span>
                    <NdStar isNd={subItem.isNd} />
                  </div>
                  <div className="text-xs text-gray-400 dark:text-graphite-faint">{subItem.code}</div>
                </div>
              </div>
            </td>

            {/* Current Stock */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs"
              style={{ minWidth: "120px", width: "12%" }}
            >
              <div className={`font-medium ${getStockValueColorClass(subItem.severity)}`}>
                {formatWarehouseStock(subItem)}
              </div>
            </td>

            {/* Reserve Stock */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs text-gray-700 dark:text-graphite-muted"
              style={{ minWidth: "90px", width: "10%" }}
            >
              {(subItem.reserve || 0) > 0 ? (
                <div className="font-medium">
                  {formatNumber(subItem.reserve, 0)}
                </div>
              ) : (
                <span className="text-gray-400 dark:text-graphite-faint">—</span>
              )}
            </td>

            {/* Quarantine Stock */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs text-gray-700 dark:text-graphite-muted"
              style={{ minWidth: "90px", width: "10%" }}
            >
              {(subItem.quarantine || 0) > 0 ? (
                <div className="font-medium text-orange-700 dark:text-orange-300">
                  {formatNumber(subItem.quarantine, 0)}
                </div>
              ) : (
                <span className="text-gray-400 dark:text-graphite-faint">—</span>
              )}
            </td>

            {/* Planned Stock */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs text-gray-700 dark:text-graphite-muted"
              style={{ minWidth: "90px", width: "10%" }}
            >
              {(subItem.planned || 0) > 0 ? (
                <div className="font-medium">
                  {formatNumber(subItem.planned, 0)}
                </div>
              ) : (
                <span className="text-gray-400 dark:text-graphite-faint">—</span>
              )}
            </td>

            {/* Sales in Period */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs text-gray-700 dark:text-graphite-muted"
              style={{ minWidth: "100px", width: "12%" }}
            >
              {formatNumber(subItem.salesInPeriod, 0)}
            </td>

            {/* Daily Sales */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs text-gray-700 dark:text-graphite-muted"
              style={{ minWidth: "100px", width: "12%" }}
            >
              {formatNumber(subItem.dailySalesRate, 2)}
            </td>

            {/* Optimal Days Setup */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs text-gray-700 dark:text-graphite-muted"
              style={{ minWidth: "90px", width: "10%" }}
            >
              {subItem.optimalDaysSetup > 0
                ? `${subItem.optimalDaysSetup} dní`
                : "—"}
            </td>

            {/* Stock Days Available */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs text-gray-700 dark:text-graphite-muted"
              style={{ minWidth: "90px", width: "10%" }}
            >
              <div className="font-medium">
                {subItem.stockDaysAvailable > 999
                  ? "∞"
                  : formatNumber(subItem.stockDaysAvailable, 0)}
              </div>
            </td>

            {/* Minimum Stock */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs text-gray-700 dark:text-graphite-muted"
              style={{ minWidth: "90px", width: "10%" }}
            >
              {formatNumber(subItem.minimumStock, 0)}
            </td>

            {/* Overstock Percentage */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs text-gray-700 dark:text-graphite-muted"
              style={{ minWidth: "90px", width: "10%" }}
            >
              {formatPercentage(subItem.overstockPercentage)}
            </td>

            {/* Batch Size */}
            <td
              className="px-3 py-3 whitespace-nowrap text-right text-xs text-gray-700 dark:text-graphite-muted"
              style={{ minWidth: "80px", width: "8%" }}
            >
              {subItem.batchSize || "—"}
            </td>

            {/* Planning List Button */}
            <td
              className="px-2 py-3 whitespace-nowrap text-center"
              style={{ minWidth: "60px", width: "5%" }}
            >
              <button
                onClick={(e) => handleTogglePlanningList(subItem, e)}
                className={`p-1 rounded transition-colors ${
                  isInPlanningList(subItem.code)
                    ? "text-green-600 dark:text-emerald-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                    : "text-gray-400 dark:text-graphite-faint hover:text-indigo-600 dark:hover:text-graphite-accent hover:bg-indigo-50 dark:hover:bg-graphite-accent/10"
                }`}
                title={
                  isInPlanningList(subItem.code)
                    ? "Odebrat ze seznamu k plánování"
                    : "Přidat do seznamu k plánování"
                }
              >
                {isInPlanningList(subItem.code) ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
              </button>
            </td>
          </tr>
        ))}
      </>
    );
  };

  // Get color strip for product based on severity - matching summary cards colors
  const getSeverityStripColor = (severity: ManufacturingStockSeverity | undefined) => {
    // Handle both string and numeric enum values
    const severityStr = String(severity);
    
    switch (severityStr) {
      case "Critical":
      case "0":
        return "bg-red-500";
      case "Major":
      case "1":
        return "bg-orange-500";
      case "Minor":
      case "2":
        return "bg-yellow-500";
      case "Adequate":
      case "3":
        return "bg-green-500";
      case "Unconfigured":
      case "4":
        return "bg-gray-500";
      default:
        return "bg-gray-400";
    }
  };


  // Get color class for stock values based on severity - matching summary cards
  const getStockValueColorClass = (severity: ManufacturingStockSeverity | undefined) => {
    switch (severity) {
      case ManufacturingStockSeverity.Critical:
        return "text-red-600 dark:text-red-400";    // Matches summary card text
      case ManufacturingStockSeverity.Major:
        return "text-orange-600 dark:text-orange-300"; // Matches summary card text
      case ManufacturingStockSeverity.Minor:
        return "text-yellow-600 dark:text-amber-400"; // Minor items
      case ManufacturingStockSeverity.Adequate:
        return "text-green-600 dark:text-emerald-400";  // Matches summary card text
      case ManufacturingStockSeverity.Unconfigured:
        return "text-gray-600 dark:text-graphite-muted";   // Matches summary card text
      default:
        return "text-gray-900 dark:text-graphite-text";
    }
  };

  // Get time period tooltip
  const getTimePeriodTooltip = (timePeriod: TimePeriodFilter) => {
    if (
      timePeriod === TimePeriodFilter.CustomPeriod &&
      filters.customFromDate &&
      filters.customToDate
    ) {
      return `${filters.customFromDate.toLocaleDateString("cs-CZ")} - ${filters.customToDate.toLocaleDateString("cs-CZ")}`;
    }

    const range = calculateTimePeriodRange(timePeriod);
    if (!range?.fromDate || !range?.toDate) {
      return getTimePeriodDisplayText(timePeriod);
    }
    if (range.ranges && range.ranges.length > 1) {
      return range.ranges
        .map(
          (r, i) =>
            `Období ${String.fromCharCode(65 + i)}: ${r.from.toLocaleDateString("cs-CZ")} – ${r.to.toLocaleDateString("cs-CZ")}`,
        )
        .join("\n");
    }
    return `${range.fromDate.toLocaleDateString("cs-CZ")} - ${range.toDate.toLocaleDateString("cs-CZ")}`;
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-graphite-surface-2 px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded-lg p-6">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <h3 className="text-lg font-medium text-red-800 dark:text-red-300">
                Chyba při načítání dat
              </h3>
            </div>
            <p className="mt-2 text-sm text-red-700 dark:text-red-300">
              {error instanceof Error ? error.message : "Neočekávaná chyba"}
            </p>
            <button
              onClick={() => refetch()}
              className="mt-4 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-800 dark:text-red-300 px-4 py-2 rounded-md text-sm font-medium"
            >
              Zkusit znovu
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col w-full"
      style={{ height: PAGE_CONTAINER_HEIGHT }}
    >
      {/* Header - Fixed */}
      <div className="flex-shrink-0 mb-3">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-graphite-text">
          Řízení zásob výrobků
        </h1>
      </div>

      {/* Controls - Single Collapsible Block */}
      <div className="flex-shrink-0 bg-white dark:bg-graphite-surface rounded-lg shadow dark:shadow-soft-dark mb-4">
        <div className="p-3 border-b border-gray-200 dark:border-graphite-border">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsControlsCollapsed(!isControlsCollapsed)}
              className="flex items-center space-x-2 text-sm font-medium text-gray-900 dark:text-graphite-text hover:text-gray-700 dark:hover:text-graphite-muted"
            >
              {isControlsCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <span>Filtry a nastavení</span>
              {summary && (
                <span className="text-xs text-gray-500 dark:text-graphite-muted">
                  ({summary.totalProducts} produktů)
                </span>
              )}
            </button>

            <div className="flex items-center space-x-3">
              {/* Always visible controls when collapsed */}
              {isControlsCollapsed && (
                <>
                  {/* Quick summary when collapsed - clickable */}
                  {summary && (
                    <div className="flex items-center space-x-2 text-xs">
                      <button
                        onClick={() =>
                          handleSeverityFilterClick(
                            ManufacturingStockSeverity.Critical,
                          )
                        }
                        className={`px-1 py-0.5 rounded transition-colors hover:bg-red-50 dark:hover:bg-red-900/30 ${
                          filters.criticalItemsOnly
                            ? "bg-red-50 dark:bg-red-900/30 ring-1 ring-red-300 dark:ring-red-900/40"
                            : ""
                        }`}
                        title="Nadsklad &lt; 100%"
                      >
                        <span className="text-red-600 dark:text-red-400 font-medium">
                          {summary.criticalCount}
                        </span>
                      </button>
                      <button
                        onClick={() =>
                          handleSeverityFilterClick(
                            ManufacturingStockSeverity.Major,
                          )
                        }
                        className={`px-1 py-0.5 rounded transition-colors hover:bg-orange-50 dark:hover:bg-orange-900/30 ${
                          filters.majorItemsOnly
                            ? "bg-orange-50 dark:bg-orange-900/30 ring-1 ring-orange-300 dark:ring-orange-900/40"
                            : ""
                        }`}
                        title="Pod minimální zásobou"
                      >
                        <span className="text-orange-600 dark:text-orange-300 font-medium">
                          {summary.majorCount}
                        </span>
                      </button>
                      <button
                        onClick={() =>
                          handleSeverityFilterClick(
                            ManufacturingStockSeverity.Adequate,
                          )
                        }
                        className={`px-1 py-0.5 rounded transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/30 ${
                          filters.adequateItemsOnly
                            ? "bg-emerald-50 dark:bg-emerald-900/30 ring-1 ring-emerald-300 dark:ring-emerald-900/40"
                            : ""
                        }`}
                        title="OK"
                      >
                        <span className="text-green-600 dark:text-emerald-400 font-medium">
                          {summary.adequateCount}
                        </span>
                      </button>
                    </div>
                  )}
                  {/* Search field when collapsed */}
                  <div className="flex-1 max-w-xs">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 dark:text-graphite-faint" />
                      <input
                        type="text"
                        value={filters.searchTerm || ""}
                        onChange={(e) =>
                          handleFilterChange({ searchTerm: e.target.value })
                        }
                        placeholder="Vyhledat..."
                        className="pl-7 w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Sales multiplier control - always visible */}
              <div
                className="flex items-center border border-gray-300 dark:border-graphite-border rounded-md shadow-sm dark:shadow-soft-dark bg-white dark:bg-graphite-surface"
                title="Násobitel denních prodejů pro simulaci poptávky"
              >
                <button
                  onClick={() => handleSalesMultiplierChange(-0.1)}
                  disabled={(filters.salesMultiplier ?? 1.0) <= 0.1}
                  className="px-1.5 py-1 text-xs text-gray-700 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 rounded-l-md"
                  aria-label="Snížit násobitel"
                >
                  −
                </button>
                <span
                  className={`px-1.5 py-1 text-xs min-w-[2.5rem] text-center ${
                    (filters.salesMultiplier ?? 1.0) !== 1.0
                      ? "text-indigo-600 dark:text-graphite-accent font-semibold"
                      : "text-gray-700 dark:text-graphite-muted"
                  }`}
                  title={
                    (filters.salesMultiplier ?? 1.0) !== 1.0
                      ? "Prodejní násobitel aktivní"
                      : undefined
                  }
                >
                  {((filters.salesMultiplier ?? 1.0).toFixed(1))}x
                </span>
                <button
                  onClick={() => handleSalesMultiplierChange(0.1)}
                  disabled={(filters.salesMultiplier ?? 1.0) >= 3.0}
                  className="px-1.5 py-1 text-xs text-gray-700 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-40 rounded-r-md"
                  aria-label="Zvýšit násobitel"
                >
                  +
                </button>
              </div>

              {/* Action buttons - always visible */}
              <button
                onClick={() => refetch()}
                disabled={isRefetching}
                className="flex items-center px-2 py-1 border border-gray-300 dark:border-graphite-border rounded-md shadow-sm dark:shadow-soft-dark text-xs font-medium text-gray-700 dark:text-graphite-muted bg-white dark:bg-graphite-surface hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-3 w-3 mr-1 ${isRefetching ? "animate-spin" : ""}`}
                />
                {isControlsCollapsed ? "" : "Obnovit"}
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="flex items-center px-2 py-1 border border-gray-300 dark:border-graphite-border rounded-md shadow-sm dark:shadow-soft-dark text-xs font-medium text-gray-700 dark:text-graphite-muted bg-white dark:bg-graphite-surface hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50"
              >
                {isExporting ? (
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Download className="h-3 w-3 mr-1" />
                )}
                {isControlsCollapsed ? "" : "Export"}
              </button>

              <ColumnChooser
                columns={columns}
                columnState={columnState}
                onToggle={toggleColumnVisibility}
                onReset={resetLayout}
              />

              {/* Help */}
              <div className="relative group">
                <HelpCircle className="h-4 w-4 text-gray-400 dark:text-graphite-faint cursor-help" />
                <div className="absolute right-0 top-6 w-80 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <div className="w-3 h-3 bg-red-200 rounded-sm mr-2 mt-0.5 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-red-200">
                          Červené:
                        </span>{" "}
                        Nadsklad &lt; 100%
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-3 h-3 bg-orange-200 rounded-sm mr-2 mt-0.5 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-orange-200">
                          Oranžové:
                        </span>{" "}
                        Skladem &lt; minimální zásoba
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-3 h-3 bg-gray-200 rounded-sm mr-2 mt-0.5 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-gray-200">Šedé:</span>{" "}
                        Nezkonfigurováno (chybí OptimalStockDaysSetup)
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-3 h-3 bg-emerald-200 rounded-sm mr-2 mt-0.5 flex-shrink-0"></div>
                      <div>
                        <span className="font-medium text-emerald-200">
                          Zelené:
                        </span>{" "}
                        Všechny podmínky OK
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {!isControlsCollapsed && (
          <div className="p-3 space-y-4">
            {/* Summary Cards */}
            {summary && (
              <div>
                <h3 className="text-xs font-medium text-gray-700 dark:text-graphite-muted mb-2">
                  Přehled stavů zásob
                </h3>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <div className="flex items-center px-2 py-1 rounded-md bg-gray-50 dark:bg-graphite-surface-2">
                    <Package className="h-3 w-3 text-blue-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">Celkem:</span>
                    <span className="font-semibold text-gray-900 dark:text-graphite-text ml-1">
                      {summary.totalProducts}
                    </span>
                  </div>

                  <button
                    onClick={() =>
                      handleSeverityFilterClick(
                        ManufacturingStockSeverity.Critical,
                      )
                    }
                    className={`flex items-center px-2 py-1 rounded-md transition-colors hover:bg-red-50 dark:hover:bg-red-900/30 ${
                      filters.criticalItemsOnly
                        ? "bg-red-50 dark:bg-red-900/30 ring-1 ring-red-300 dark:ring-red-900/40"
                        : ""
                    }`}
                  >
                    <AlertTriangle className="h-3 w-3 text-red-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">Nadsklad &lt; 100%:</span>
                    <span className="font-semibold text-red-600 dark:text-red-400 ml-1">
                      {summary.criticalCount}
                    </span>
                  </button>

                  <button
                    onClick={() =>
                      handleSeverityFilterClick(
                        ManufacturingStockSeverity.Major,
                      )
                    }
                    className={`flex items-center px-2 py-1 rounded-md transition-colors hover:bg-orange-50 dark:hover:bg-orange-900/30 ${
                      filters.majorItemsOnly
                        ? "bg-orange-50 dark:bg-orange-900/30 ring-1 ring-orange-300 dark:ring-orange-900/40"
                        : ""
                    }`}
                  >
                    <AlertCircle className="h-3 w-3 text-orange-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">Pod min. zásobou:</span>
                    <span className="font-semibold text-orange-600 dark:text-orange-300 ml-1">
                      {summary.majorCount}
                    </span>
                  </button>

                  <button
                    onClick={() =>
                      handleSeverityFilterClick(
                        ManufacturingStockSeverity.Adequate,
                      )
                    }
                    className={`flex items-center px-2 py-1 rounded-md transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/30 ${
                      filters.adequateItemsOnly
                        ? "bg-emerald-50 dark:bg-emerald-900/30 ring-1 ring-emerald-300 dark:ring-emerald-900/40"
                        : ""
                    }`}
                  >
                    <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">OK:</span>
                    <span className="font-semibold text-green-600 dark:text-emerald-400 ml-1">
                      {summary.adequateCount}
                    </span>
                  </button>

                  <button
                    onClick={() =>
                      handleSeverityFilterClick(
                        ManufacturingStockSeverity.Unconfigured,
                      )
                    }
                    className={`flex items-center px-2 py-1 rounded-md transition-colors hover:bg-gray-50 dark:hover:bg-white/5 ${
                      filters.unconfiguredOnly
                        ? "bg-gray-50 dark:bg-graphite-surface-2 ring-1 ring-gray-300 dark:ring-graphite-border"
                        : ""
                    }`}
                  >
                    <Settings className="h-3 w-3 text-gray-500 mr-1" />
                    <span className="text-gray-600 dark:text-graphite-muted">Nezkonfigurováno:</span>
                    <span className="font-semibold text-gray-600 dark:text-graphite-muted ml-1">
                      {summary.unconfiguredCount}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div>
              <h3 className="text-xs font-medium text-gray-700 dark:text-graphite-muted mb-2">Filtry</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Time Period Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-graphite-muted mb-1">
                    Časové období
                  </label>
                  <select
                    value={
                      filters.timePeriod || TimePeriodFilter.Q9M
                    }
                    onChange={(e) =>
                      handleTimePeriodChange(e.target.value as TimePeriodFilter)
                    }
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                    title={getTimePeriodTooltip(
                      filters.timePeriod || TimePeriodFilter.Q9M,
                    )}
                  >
                    <option value={TimePeriodFilter.PreviousQuarter}>
                      Minulý kvartal
                    </option>
                    <option value={TimePeriodFilter.FutureQuarter}>
                      Budoucí kvartal
                    </option>
                    <option value={TimePeriodFilter.Y2Y}>
                      Y2Y (12 měsíců)
                    </option>
                    <option value={TimePeriodFilter.PreviousSeason}>
                      Předchozí sezona
                    </option>
                    <option value={TimePeriodFilter.Q9M}>
                      9M (6 měsíců + prognóza 3 měsíce)
                    </option>
                    <option value={TimePeriodFilter.CustomPeriod}>
                      Vlastní období
                    </option>
                  </select>
                </div>

                {/* Custom Date Range (only show if CustomPeriod is selected) */}
                {filters.timePeriod === TimePeriodFilter.CustomPeriod && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-graphite-muted mb-1">
                        Od data
                      </label>
                      <input
                        type="date"
                        value={
                          filters.customFromDate?.toISOString().split("T")[0] ||
                          ""
                        }
                        onChange={(e) =>
                          handleFilterChange({
                            customFromDate: new Date(e.target.value),
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-graphite-muted mb-1">
                        Do data
                      </label>
                      <input
                        type="date"
                        value={
                          filters.customToDate?.toISOString().split("T")[0] ||
                          ""
                        }
                        onChange={(e) =>
                          handleFilterChange({
                            customToDate: new Date(e.target.value),
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                      />
                    </div>
                  </>
                )}

                {/* Product Family */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-graphite-muted mb-1">
                    Produktová řada
                  </label>
                  <select
                    value={filters.productFamily || ""}
                    onChange={(e) =>
                      handleFilterChange({
                        productFamily: e.target.value || undefined,
                      })
                    }
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                  >
                    <option value="">Všechny</option>
                    {summary?.productFamilies?.map((family) => (
                      <option key={family} value={family}>
                        {family}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-graphite-muted mb-1">
                    Vyhledat
                  </label>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 dark:text-graphite-faint" />
                    <input
                      type="text"
                      value={filters.searchTerm || ""}
                      onChange={(e) =>
                        handleFilterChange({ searchTerm: e.target.value })
                      }
                      placeholder="Kód, název, řada..."
                      className="pl-8 w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text dark:placeholder-graphite-faint"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Table */}
      <div className="flex-1 bg-white dark:bg-graphite-surface rounded-lg shadow dark:shadow-soft-dark overflow-hidden flex flex-col min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-gray-400 dark:text-graphite-faint" />
            <span className="ml-2 text-gray-600 dark:text-graphite-muted">Načítání dat...</span>
          </div>
        ) : tableData.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Package className="h-12 w-12 text-gray-400 dark:text-graphite-faint mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-graphite-text mb-2">
                Žádné výsledky
              </h3>
              <p className="text-gray-600 dark:text-graphite-muted">
                Zkuste upravit filtry nebo vyhledávací kritéria.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-x-auto overflow-y-auto">
            <table
              className="w-full divide-y divide-gray-200 dark:divide-graphite-border"
              style={{ minWidth: "1200px" }}
            >
              <GridHeader
                columns={orderedColumns}
                columnState={columnState}
                activeSortKey={filters.sortBy as string | undefined}
                sortDescending={filters.sortDescending}
                onSort={(key) => handleSort(key as ManufacturingStockSortBy)}
                onReorder={setColumnOrder}
                onResizeChange={setColumnWidthLive}
                onResizeEnd={commitColumnWidth}
              />
              <tbody className="bg-white dark:bg-graphite-surface divide-y divide-gray-200 dark:divide-graphite-border">
                {tableData.map((item) => {
                  const hasSubItems = shouldShowExpandButton(item);
                  const isExpanded =
                    item.productFamily && expandedRows.has(item.productFamily);
                  const isLoading = !!(
                    item.productFamily &&
                    loadingSubgrids.has(item.productFamily)
                  );

                  return (
                    <React.Fragment key={item.code}>
                      <tr
                        className={`${getRowColorClass(item.severity)} cursor-pointer transition-colors duration-150`}
                        onClick={(e) => handleRowClick(item, e)}
                        title="Klikněte pro zobrazení detailu produktu"
                      >
                        {orderedColumns.map((col) => {
                          const colState = columnState.find((s) => s.id === col.id);
                          const width = colState?.width ?? col.defaultWidth;
                          return (
                            <td
                              key={col.id}
                              className={`px-4 py-3 whitespace-nowrap ${col.cellClassName ?? ''}`}
                              style={width ? { width: `${width}px`, minWidth: `${col.minWidth ?? 60}px` } : undefined}
                            >
                              {col.renderCell(item)}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Subgrid for ProductFamily - only show if expanded */}
                      {hasSubItems && isExpanded && (
                        <ProductFamilySubgrid
                          productFamily={item.productFamily!}
                          isLoading={isLoading}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination - Compact */}
        {totalCount > 0 && (
          <div className="flex-shrink-0 bg-white dark:bg-graphite-surface px-3 py-2 flex items-center justify-between border-t border-gray-200 dark:border-graphite-border text-xs">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(filters.pageNumber! - 1)}
                disabled={filters.pageNumber! <= 1}
                className="relative inline-flex items-center px-2 py-1 border border-gray-300 dark:border-graphite-border text-xs font-medium rounded text-gray-700 dark:text-graphite-muted bg-white dark:bg-graphite-surface hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Předchozí
              </button>
              <button
                onClick={() => handlePageChange(filters.pageNumber! + 1)}
                disabled={filters.pageNumber! >= totalPages}
                className="ml-2 relative inline-flex items-center px-2 py-1 border border-gray-300 dark:border-graphite-border text-xs font-medium rounded text-gray-700 dark:text-graphite-muted bg-white dark:bg-graphite-surface hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Další
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div className="flex items-center space-x-3">
                <p className="text-xs text-gray-600 dark:text-graphite-muted">
                  {(filters.pageNumber! - 1) * filters.pageSize! + 1}-
                  {Math.min(
                    filters.pageNumber! * filters.pageSize!,
                    totalCount,
                  )}{" "}
                  z {totalCount}
                </p>
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-gray-600 dark:text-graphite-muted">Zobrazit:</span>
                  <select
                    value={filters.pageSize}
                    onChange={(e) =>
                      handlePageSizeChange(Number(e.target.value))
                    }
                    className="border border-gray-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-transparent dark:bg-graphite-surface-2 dark:border-graphite-border dark:text-graphite-text"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
              <div>
                <nav
                  className="relative z-0 inline-flex rounded shadow-sm -space-x-px"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => handlePageChange(filters.pageNumber! - 1)}
                    disabled={filters.pageNumber! <= 1}
                    className="relative inline-flex items-center px-1 py-1 rounded-l border border-gray-300 dark:border-graphite-border bg-white dark:bg-graphite-surface text-xs font-medium text-gray-500 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-3 w-3" />
                  </button>

                  {/* Page numbers */}
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (filters.pageNumber! <= 3) {
                      pageNum = i + 1;
                    } else if (filters.pageNumber! >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = filters.pageNumber! - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-2 py-1 border text-xs font-medium ${
                          pageNum === filters.pageNumber!
                            ? "z-10 bg-indigo-50 dark:bg-graphite-accent/10 border-indigo-500 dark:border-graphite-accent text-indigo-600 dark:text-graphite-accent"
                            : "bg-white dark:bg-graphite-surface border-gray-300 dark:border-graphite-border text-gray-500 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(filters.pageNumber! + 1)}
                    disabled={filters.pageNumber! >= totalPages}
                    className="relative inline-flex items-center px-1 py-1 rounded-r border border-gray-300 dark:border-graphite-border bg-white dark:bg-graphite-surface text-xs font-medium text-gray-500 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-3 w-3" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Product Detail Modal */}
      <CatalogDetail
        productCode={selectedProductCode}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetail}
        defaultTab="history"
      />

      {/* Planning List Panel */}
      <PlanningListPanel isVisible={false} />
    </div>
  );
};

export default ManufacturingStockAnalysis;
